import React, { useMemo, ReactElement } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getAmountButtonTheme } from '../../style';
import { IDefaultButton, IView } from './types/OfferAmountButton';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import breakpoints from '../../theme/material/breakpoints';
import { FONT_FAMILIES } from 'theme/material/constants';
import { isDarkMode } from 'utils/helpers';

const getBaseStyles = () => ({
  color: '#E2C92D !important',
  padding: 10,
  background: getAmountButtonTheme().backgroundStyle,
  borderRadius: '5px',
  width: 105,
  border: getAmountButtonTheme().borderStyle,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.16)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 10,
  [breakpoints.down(768.05)]: {
    borderWidth: 2,
    width: 80,
    padding: 8,
  },
});

const gridStyles = {
  margin: 'auto 0',
};

const listStyles = type => ({
  margin: '5px 10px',
  [breakpoints.between(800, 821)]: {
    height: '72px !important',
  },
  [breakpoints.down(639.05)]: {
    margin: type === 'sent' ? 0 : '0px 10px',
  },
});

const hoverStyles = {
  '&:hover': {
    border: getAmountButtonTheme().borderHoverStyle,
    backgroundColor: '#2490DE',
    '> label': {
      color: '#fff',
    },
  },
};

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const DefaultButton = styled('div')<IDefaultButton>(
  (): any => ({
    ...getBaseStyles(),
  }),
  ({ viewType, type }) => (viewType === 'grid' ? gridStyles : listStyles(type)),
  ({ hasHoverEffect }) => hasHoverEffect && hoverStyles,
  ({ customBoxStyle }) => customBoxStyle
);

const OfferLabel = styled('label')<IView>(
  () => ({
    color: isDarkMode() ? '#E2C92D' : '#8F8F8F',
    fontSize: 12,
    lineHeight: '8px',
    letterSpacing: '-0.3px',
    [breakpoints.down('sm')]: {
      fontSize: 10,
    },
  }),
  ({ viewType }) =>
    viewType === 'list' && {
      [breakpoints.down(426.05)]: {
        fontSize: 11,
      },
    }
);

const Amount = styled('label')<IView>(
  () => ({
    fontFamily: FONT_FAMILIES.secondary,
    color: getAmountButtonTheme().color,
    fontSize: 45,
    lineHeight: '30px',
    letterSpacing: '-0.3px',
    [breakpoints.down(768.05)]: {
      fontSize: 32,
    },
  }),
  ({ viewType }) =>
    viewType === 'list' && {
      [breakpoints.down(426.05)]: {
        fontSize: 32,
      },
    }
);

const EmptyAmount = styled('label')<IView>(
  (): CSSObject => ({
    color: getAmountButtonTheme().color,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '-0.02em',
    maxWidth: 83,
    textAlign: 'center',
  }),
  ({ viewType }) =>
    viewType === 'list' && {
      [breakpoints.down(426.05)]: {
        fontSize: 12,
      },
    }
);

interface OfferAmountButtonV2Props {
  price: string;
  type: string;
  hasHoverEffect: boolean;
  viewType: any;
  forceLabel?: string;
  onClick: () => void;
  customBoxStyle?: React.CSSProperties;
}

// temporary replace to v2 since this button is also used in profile page
const OfferAmountButtonV2 = ({
  price,
  type,
  hasHoverEffect = false,
  forceLabel,
  ...rest
}: OfferAmountButtonV2Props): ReactElement => {
  const { isMobile } = useMediaBreakpoints();

  const receivedLabel = useMemo(() => (isMobile ? 'Suggest an Amount' : 'Asked You to Suggest an Amount'), [isMobile]);
  const style = useMemo(() => (hasHoverEffect ? { cursor: 'pointer' } : {}), [hasHoverEffect]);
  const renderLabel = type === 'received' ? receivedLabel : 'No Amount Suggested';

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <DefaultButton style={style} className="offer-amount-button" {...rest} hasHoverEffect={hasHoverEffect} type={type}>
      {price !== '' ? (
        <>
          <Amount
            style={style}
            className="offer-amount-label"
            viewType={rest.viewType}
            data-test-id={'btn-amount-price'}
          >
            {price}
          </Amount>
          <OfferLabel style={style} className="offer-amount-label2" viewType={rest.viewType}>
            Offer Amount
          </OfferLabel>
        </>
      ) : (
        <EmptyAmount style={style} className="suggest-amount-label" viewType={rest.viewType}>
          {forceLabel ? forceLabel : renderLabel}
        </EmptyAmount>
      )}
    </DefaultButton>
  );
};

export default OfferAmountButtonV2;
